import { logger } from './logger';

export interface SessionInvalidationEvent {
  readonly status: 401 | 403;
  readonly message?: string;
  readonly context?: string;
}

type SessionInvalidationListener = (
  event: SessionInvalidationEvent,
) => void | Promise<void>;

const listeners = new Set<SessionInvalidationListener>();
let invalidationInFlight = false;

export function shouldInvalidateSession(
  status: number | undefined,
  logoutRequired?: boolean,
): status is 401 | 403 {
  if (status === 401) {
    return true;
  }
  return status === 403 && logoutRequired !== false;
}

export function notifySessionInvalidated(event: SessionInvalidationEvent): boolean {
  if (invalidationInFlight) {
    return false;
  }

  invalidationInFlight = true;
  logger.event(
    'auth.session.invalidated',
    {
      status: event.status,
      context: event.context,
    },
    'warn',
    'Authenticated session invalidated by API response',
  );

  if (listeners.size === 0) {
    invalidationInFlight = false;
    return true;
  }

  listeners.forEach((listener) => {
    void Promise.resolve(listener(event)).catch((error) => {
      logger.error('Session invalidation listener failed', error, {
        event: 'auth.session.invalidation_listener_failed',
      });
      completeSessionInvalidation();
    });
  });

  return true;
}

export function subscribeToSessionInvalidation(
  listener: SessionInvalidationListener,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function completeSessionInvalidation(): void {
  invalidationInFlight = false;
}

export function resetSessionInvalidationForTests(): void {
  listeners.clear();
  invalidationInFlight = false;
}
